<template>
  <TemplateHome :page="home?.page" />
</template>

<script setup lang="ts">
import { seoHead, getIndirectClientHeaders } from '~/assets/seo'
import { pathWithoutHash, setCacheControlHeader } from '~/assets/util'

// Capture composables before any await (SSR request-isolation pattern).
const event = useRequestEvent()
const route = useRoute()
const pageStore = usePageStore()
const globalStore = useGlobalStore()
const tixStore = useTixStore()

/*
 * The home entry is looked up by the path being served, not by a fixed `/`: each locale has its own home
 * entry in the CMS, addressed by its own uri, so the English one is only found under `/en`. The key
 * carries the path for the same reason, since a single key would hand one locale's payload to the other.
 */
const fullPath = pathWithoutHash(route.fullPath)

const { data: home } = await useAsyncData(`home;${fullPath}`, async () => {
  const [, globalSeo, page] = await Promise.all([
    globalStore.fetchNavigation(),
    globalStore.fetchSeo(),
    pageStore.fetchUri({ uri: decodeURI(fullPath), push: true, options: { headers: getIndirectClientHeaders({ req: event?.node.req }) } }),
  ])
  return { globalSeo, page }
})

if (!home.value?.page) {
  throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
}

if (home.value.page._redirect_) {
  await navigateTo(home.value.page._redirect_.to, { redirectCode: home.value.page._redirect_.type, external: true })
}

setCacheControlHeader(event)

tixStore.setTixEnv('tix')

useHead(() => seoHead(home.value?.page, home.value?.globalSeo))
</script>
